import { ValidateEmbedResponseDtoValidationStatusEnum } from '@proposify/document-service-api-client';
import type { DimensionPositionStateChangedType } from 'components/editor/hooks/UseBlockDimensionChangedHandler';
import { googleSheetsEmbedConfig } from './googleSheetsEmbed';
import { microsoftExcelEmbedConfig } from './microsoftExcelEmbed';

export { extractEmbedSrc } from './embedCode';

export type EmbedSubtype = 'GOOGLE_SHEETS' | 'MICROSOFT_EXCEL';

export type EmbedValidationStatus = keyof typeof ValidateEmbedResponseDtoValidationStatusEnum;

const EMBED_SUBTYPES: EmbedSubtype[] = ['GOOGLE_SHEETS', 'MICROSOFT_EXCEL'];
const EMBED_VALIDATION_STATUSES = Object.keys(ValidateEmbedResponseDtoValidationStatusEnum) as EmbedValidationStatus[];

export const isEmbedSubtype = (value: string): value is EmbedSubtype => EMBED_SUBTYPES.includes(value as EmbedSubtype);

export const isEmbedValidationStatus = (value: string): value is EmbedValidationStatus =>
  EMBED_VALIDATION_STATUSES.includes(value as EmbedValidationStatus);

export type EmbedContent = {
  embedUrl: string;
  subtype?: EmbedSubtype;
  validationStatus?: EmbedValidationStatus;
  snapshotUrl?: string | null;
  snapshotTimestamp?: string | null;
  aspectRatioLocked?: boolean;
};

export type ParsedEmbedCode = {
  embedUrl: string;
  dimensions: { width: number; height: number } | null;
};

export type EmbedProviderConfig = {
  providerName: string;
  titleKey: string;
  step1InstructionsKey: string;
  descriptionKey: string;
  inputLabelKey: string;
  docsUrl: string;
  image: string;
  sandbox: string;
  parser: (embedCode: string) => ParsedEmbedCode;
};

export const ERROR_TRANSLATION_KEY: Partial<Record<EmbedValidationStatus, string>> = {
  REQUIRES_LOGIN: 'document.grid.embed.dialog_error_requires_login',
  BLOCKED_BY_DOMAIN: 'document.grid.embed.dialog_error_blocked_by_domain',
  TIMEOUT: 'document.grid.embed.dialog_error_timeout',
};

export const EMBED_PROVIDER_CONFIG: Record<EmbedSubtype, EmbedProviderConfig> = {
  GOOGLE_SHEETS: googleSheetsEmbedConfig,
  MICROSOFT_EXCEL: microsoftExcelEmbedConfig,
};

export const parseEmbedCode = (embedUrl: string, subtype: EmbedSubtype): ParsedEmbedCode => EMBED_PROVIDER_CONFIG[subtype].parser(embedUrl);

export const toEmbedDimensionChange = (
  current: { x: number; y: number; width: number; height: number },
  next: { width: number; height: number }
): [DimensionPositionStateChangedType, DimensionPositionStateChangedType] => [
  { widthPx: current.width, heightPx: current.height, xAxisPx: current.x, yAxisPx: current.y },
  { widthPx: next.width, heightPx: next.height, xAxisPx: current.x, yAxisPx: current.y },
];
